import React from "react";
import { Link } from "react-router-dom";

export function AuthShell({ title, subtitle, onSubmit, error, loading, submitLabel, children, footer }) {
  return (
    <div className="min-h-screen bg-bg flex items-center justify-center px-4 py-20 relative overflow-hidden">
      {/* Background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-64 bg-accent rounded-full blur-[120px] opacity-[0.07] pointer-events-none" />

      <div className="relative z-10 w-full max-w-md bg-surface border border-white/[0.07] rounded-2xl px-8 py-10 shadow-[0_40px_80px_rgba(0,0,0,0.5)]">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 font-display font-bold text-lg mb-8">
          <span className="text-accent text-sm">✦</span>
          RoomViz<span className="text-accent2">AI</span>
        </Link>

        <h1 className="font-display font-extrabold text-2xl tracking-tight mb-1">{title}</h1>
        <p className="text-sm text-t2 mb-8">{subtitle}</p>

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        <form onSubmit={onSubmit} className="flex flex-col gap-5">
          {children}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-accent hover:bg-accent2 disabled:opacity-60 text-white font-semibold text-sm py-3 rounded-xl transition-all duration-200 hover:shadow-[0_0_24px_rgba(124,92,252,0.4)] flex items-center justify-center gap-2"
          >
            {loading ? (
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : submitLabel}
          </button>
        </form>

        {/* Divider */}
        <div className="flex items-center gap-4 my-6">
          <div className="flex-1 h-px bg-white/[0.07]" />
          <span className="text-xs text-t3">or continue with</span>
          <div className="flex-1 h-px bg-white/[0.07]" />
        </div>

        {/* Social */}
        <div className="grid grid-cols-2 gap-3">
          <button className="flex items-center justify-center gap-2 border border-white/[0.08] hover:border-white/20 rounded-xl py-2.5 text-sm font-medium text-t2 hover:text-t1 transition-all">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Google
          </button>
          <button className="flex items-center justify-center gap-2 border border-white/[0.08] hover:border-white/20 rounded-xl py-2.5 text-sm font-medium text-t2 hover:text-t1 transition-all">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.477 2 2 6.477 2 12c0 4.418 2.865 8.166 6.839 9.489.5.092.682-.217.682-.482 0-.237-.008-.866-.013-1.7-2.782.603-3.369-1.342-3.369-1.342-.454-1.155-1.11-1.462-1.11-1.462-.908-.62.069-.608.069-.608 1.003.07 1.531 1.03 1.531 1.03.892 1.529 2.341 1.087 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.11-4.555-4.943 0-1.091.39-1.984 1.029-2.683-.103-.253-.446-1.27.098-2.647 0 0 .84-.269 2.75 1.025A9.578 9.578 0 0112 6.836c.85.004 1.705.114 2.504.336 1.909-1.294 2.747-1.025 2.747-1.025.546 1.377.203 2.394.1 2.647.64.699 1.028 1.592 1.028 2.683 0 3.842-2.339 4.687-4.566 4.935.359.309.678.919.678 1.852 0 1.336-.012 2.415-.012 2.743 0 .267.18.578.688.48C19.138 20.161 22 16.416 22 12c0-5.523-4.477-10-10-10z"/>
            </svg>
            GitHub
          </button>
        </div>

        <p className="text-sm text-t3 text-center mt-6">{footer}</p>
      </div>
    </div>
  );
}

export function Field({ label, id, type = "text", placeholder, value, onChange, name, autoComplete, extra }) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between">
        <label htmlFor={id} className="text-sm font-medium text-t1">{label}</label>
        {extra}
      </div>
      <input
        id={id}
        name={name || id}
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        autoComplete={autoComplete}
        className="bg-bg border border-white/[0.08] focus:border-accent/50 outline-none rounded-xl px-4 py-3 text-sm text-t1 placeholder-t3 transition-colors w-full"
      />
    </div>
  );
}
